import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import CertificateDetails from '../components/CertificateDetails';
import QRCodeScanner from '../components/QRCodeScanner';
import { useCertificateApi } from '../hooks/useCertificateApi';
import { CertificateResponse } from '../services/certificateApi';
import { extractCertificateNumber } from '../utils/certificateVerification';
import '../styles/CertificateVerificationPage.css';

const CertificateVerificationPage: React.FC = () => {
  const { loading, error, verifyCertificate } = useCertificateApi();
  const location = useLocation();
  const { certificateNumber: certificateNumberParam } = useParams();
  const [certificateNumber, setCertificateNumber] = useState('');
  const [certificate, setCertificate] = useState<CertificateResponse | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const verifyByNumber = useCallback(async (certNumber: string) => {
    const result = await verifyCertificate(certNumber);

    if (result?.isFound && result.certificate) {
      setCertificate(result.certificate);
      setNotFound(false);
      return;
    }

    setNotFound(true);
    setCertificate(null);
  }, [verifyCertificate]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!certificateNumber.trim()) {
      alert('Please enter a certificate number');
      return;
    }

    setCertificate(null);
    setNotFound(false);
    await verifyByNumber(certificateNumber.trim());
  };

  const handleQrCodeDetected = async (decodedText: string) => {
    const certNumber = extractCertificateNumber(decodedText);
    if (!certNumber) {
      return;
    }

    setCertificateNumber(certNumber);
    setShowScanner(false);
    await verifyByNumber(certNumber);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const certFromQuery =
      params.get('certificateNumber')
      || params.get('certificateNo')
      || params.get('certNumber')
      || params.get('cert')
      || certificateNumberParam
      || '';

    const certNumber = extractCertificateNumber(certFromQuery);

    if (!certNumber) {
      return;
    }

    setCertificateNumber(certNumber);
    setNotFound(false);
    setCertificate(null);
    void verifyByNumber(certNumber);
  }, [certificateNumberParam, location.search, verifyByNumber]);

  const handleClear = () => {
    setCertificateNumber('');
    setCertificate(null);
    setNotFound(false);
    inputRef.current?.focus();
  };

  return (
    <div className="verification-container">
      <div className="verification-card">
        <h1 className="page-title">Verify Certificate</h1>
        <p className="page-description">
          Enter a certificate number or scan a QR code to verify authenticity.
        </p>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSearch} className="verification-form">
          <div className="search-container">
            <input
              ref={inputRef}
              type="text"
              placeholder="Enter Certificate Number (e.g., CERT-2026-001)"
              value={certificateNumber}
              onChange={(e) => setCertificateNumber(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        </form>

        <div className="scanner-section">
          <button
            className={`btn btn-secondary ${showScanner ? 'active' : ''}`}
            type="button"
            onClick={() => setShowScanner((current) => !current)}
          >
            {showScanner ? 'Close Scanner' : 'Scan QR Code'}
          </button>

          {showScanner && (
            <div className="scanner-container">
              <QRCodeScanner
                onDetected={handleQrCodeDetected}
                onClose={() => setShowScanner(false)}
              />
            </div>
          )}
        </div>

        {notFound && (
          <div className="alert alert-warning">
            Certificate not found. Please check the certificate number and try again.
          </div>
        )}

        {certificate && (
          <div className="certificate-result">
            <CertificateDetails certificate={certificate} />
            <button className="btn btn-secondary" type="button" onClick={handleClear}>
              Verify Another Certificate
            </button>
          </div>
        )}

        {!certificate && !notFound && !showScanner && (
          <div className="verification-instructions">
            <h3>Verification Paths</h3>
            <ol>
              <li>Enter the certificate number from the PDF.</li>
              <li>Scan the certificate QR code from a phone or webcam.</li>
              <li>Review the status, dates, company, and scope details.</li>
            </ol>
            <h3>Status Meanings</h3>
            <ul>
              <li>
                <strong>Valid</strong> - Certificate is active and has not expired.
              </li>
              <li>
                <strong>Expired</strong> - Certificate expiry date has passed.
              </li>
              <li>
                <strong>Suspended</strong> - Certificate has been revoked or suspended.
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default CertificateVerificationPage;
